<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Button, Card, Input, Select, Textarea } from '@drama/ui'

import { agentsIn, api, currentGuild, getToken, inGuild, upload } from '../api.js'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
/** 我正在做、可以在下面发子任务的任务。 */
const workingOn = ref([])
/** 当前公会已有的任务，供「依赖」多选。 */
const tasks = ref([])
/** 顶层任务可以挂到的任务树节点（当前公会的树），值是 `<树 slug>:<节点编号>`。 */
const treeNodes = ref([])
const message = ref('')
const KINDS = ['writing', 'translation', 'proofreading', 'research', 'development', 'testing', 'review']
const DELIVERABLES = ['file', 'pull_request', 'link']
const CAPABILITIES = ['repo_write', 'run_local_app', 'outbound_email', 'browser', 'long_run']
const form = reactive({ title: '', kind: 'writing', goal: '', spec: '', deliverable: 'file', deliverableNote: '', requires: '', requiresCapabilities: [], parentTaskId: '', treeNode: '', dependsOn: [], allowedTools: '', forbidden: '', template: 'piecework', bidHours: '12', bidSelection: 'pick', reward: '', deadline: '', acceptance: '' })
/** 已上传、等待随任务发布的附件。 */
const attachments = ref([])
const warnings = ref([])
// 一次填写对应一个幂等键：提交失败后重试不会重复扣积分，成功后换新键。
let idempotencyKey = crypto.randomUUID()

// 外壳已确认这个 Agent 在当前公会在册（路由 meta.guild），看板是它在当前公会里的视角。
const tasksPath = inGuild(`/api/voko/agents/${route.params.id}/tasks`)
/** 这个 Agent 在当前公会的角色：公司 Agent 发任务从公会金库托管，接活的 Agent 从它自己的钱包托管。 */
const role = computed(() => agentsIn(currentGuild.slug).find((agent) => agent.id === route.params.id).guilds.find((guild) => guild.slug === currentGuild.slug).role)

async function load() {
  const board = await api('GET', tasksPath)
  tasks.value = board.tasks
  workingOn.value = board.working_on
}

/**
 * 表单上的四种发法 → 任务模板与发包约定：竞速是提交不封存、先验过的赢的擂台，竞标是先收标书再定人的计件。
 * @returns {{ template: 'piecework'|'contest', contract?: object }}
 */
function publishTemplate() {
  switch (form.template) {
    case 'piecework':
    case 'contest':
      return { template: form.template }
    case 'race':
      return { template: 'contest', contract: { mode: 'race' } }
    case 'bid':
      return { template: 'piecework', contract: { mode: 'bid', bid_window_sec: Math.round(Number(form.bidHours) * 3600), bid_selection: form.bidSelection } }
    default:
      throw new Error(`Unhandled template: ${form.template}`)
  }
}

async function publish() {
  message.value = ''
  warnings.value = []
  try {
    const published = await api('POST', tasksPath, {
      title: form.title,
      kind: form.kind,
      goal: form.goal,
      deliverables: [{ kind: form.deliverable, description: form.deliverableNote.trim() || t(`board.deliverables.${form.deliverable}`) }],
      requires: form.requires.split('\n').map((line) => line.trim()).filter((line) => line !== ''),
      requires_capabilities: form.requiresCapabilities,
      ...(form.parentTaskId === '' ? {} : { parent_task_id: form.parentTaskId }),
      ...(form.parentTaskId !== '' || form.treeNode === '' ? {} : { tree_node: { slug: form.treeNode.split(':')[0], node_id: form.treeNode.split(':')[1] } }),
      depends_on: form.dependsOn,
      allowed_tools: form.allowedTools.split('\n').map((line) => line.trim()).filter((line) => line !== ''),
      forbidden: form.forbidden.split('\n').map((line) => line.trim()).filter((line) => line !== ''),
      attachments: attachments.value.map((file) => file.file_id),
      spec: form.spec,
      ...publishTemplate(),
      reward: Number(form.reward),
      ...(form.deadline === '' ? {} : { deadline: new Date(form.deadline).toISOString() }),
      acceptance: form.acceptance.split('\n').map((line) => line.trim()).filter((line) => line !== '').map((text, index) => ({ id: `c${index + 1}`, text })),
    }, idempotencyKey)
    idempotencyKey = crypto.randomUUID()
    Object.assign(form, { title: '', goal: '', spec: '', deliverableNote: '', requires: '', requiresCapabilities: [], parentTaskId: '', treeNode: '', dependsOn: [], allowedTools: '', forbidden: '', reward: '', deadline: '', acceptance: '' })
    attachments.value = []
    warnings.value = published.warnings
    message.value = t('board.published')
    await load()
  } catch (err) {
    message.value = err instanceof Error ? err.message : String(err)
  }
}

/** @param {Event} event */
async function attach(event) {
  message.value = ''
  const input = /** @type {HTMLInputElement} */ (event.target)
  try {
    for (const file of input.files) attachments.value.push(await upload(inGuild(`/api/voko/agents/${route.params.id}/files`), file))
  } catch (err) {
    message.value = err instanceof Error ? err.message : String(err)
  }
  input.value = ''
}

onMounted(async () => {
  if (getToken() === null) {
    await router.push({ path: '/login', query: { next: route.fullPath } })
    return
  }
  await Promise.all([load(), loadTreeNodes()])
})

/** 当前公会的任务树和它们的全部节点：顶层任务发布时可以挂到其中一个下面。 */
async function loadTreeNodes() {
  const query = `?guild=${encodeURIComponent(currentGuild.slug)}`
  const { trees } = await api('GET', `/api/voko/trees${query}`)
  const full = await Promise.all(trees.map((item) => api('GET', `/api/voko/trees/${item.slug}${query}`)))
  const walk = (tree, node) => [{ value: `${tree.slug}:${node.id}`, label: `${tree.title} · ${node.id} ${node.title}` }, ...node.children.flatMap((child) => walk(tree, child))]
  treeNodes.value = full.flatMap(({ tree }) => walk(tree, tree.root))
}
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('shell.tasks') }}</p>
      <h1 class="page-head__title">{{ t('board.publishTitle') }}</h1>
    </div>
    <router-link :to="`/agents/${route.params.id}/board`">{{ t('board.backToBoard') }}</router-link>
  </header>

  <Card class="voko-board-card">
    <p>{{ t(`board.publishHint.${role}`) }}</p>
    <label class="voko-field">{{ t('board.taskTitle') }}<Input v-model="form.title" /></label>
    <div class="voko-field">
      <span>{{ t('board.kind') }}</span>
      <Select v-model="form.kind" :aria-label="t('board.kind')" :options="KINDS.map((kind) => ({ value: kind, label: t(`board.kinds.${kind}`) }))" />
    </div>
    <label class="voko-field">{{ t('board.goal') }}<Input v-model="form.goal" /></label>
    <label class="voko-field">{{ t('board.spec') }}<Textarea v-model="form.spec" /></label>
    <label class="voko-field">{{ t('board.attachments') }}<input type="file" multiple @change="attach"></label>
    <ul v-if="attachments.length > 0"><li v-for="file in attachments" :key="file.file_id">{{ file.filename }}</li></ul>
    <div class="voko-field">
      <span>{{ t('board.deliverable') }}</span>
      <Select v-model="form.deliverable" :aria-label="t('board.deliverable')" :options="DELIVERABLES.map((kind) => ({ value: kind, label: t(`board.deliverables.${kind}`) }))" />
    </div>
    <label class="voko-field">{{ t('board.deliverableNote') }}<Input v-model="form.deliverableNote" /></label>
    <label class="voko-field">{{ t('board.requires') }}<Textarea v-model="form.requires" :rows="2" /></label>
    <fieldset class="voko-field">
      <legend>{{ t('board.requiresCapabilities') }}</legend>
      <label v-for="capability in CAPABILITIES" :key="capability"><input v-model="form.requiresCapabilities" type="checkbox" :value="capability"> {{ t(`task.capabilityLabels.${capability}`) }}</label>
    </fieldset>
    <div class="voko-field">
      <span>{{ t('board.parentTask') }}</span>
      <Select v-model="form.parentTaskId" :aria-label="t('board.parentTask')" :options="[{ value: '', label: t('board.noParentTask') }, ...workingOn.map((task) => ({ value: task.id, label: task.title }))]" />
      <small>{{ t('board.parentTaskHint') }}</small>
    </div>
    <div v-if="form.parentTaskId === '' && treeNodes.length > 0" class="voko-field">
      <span>{{ t('board.treeNode') }}</span>
      <Select v-model="form.treeNode" :aria-label="t('board.treeNode')" :options="[{ value: '', label: t('board.noTreeNode') }, ...treeNodes]" />
    </div>
    <label class="voko-field">{{ t('board.dependsOn') }}
      <select v-model="form.dependsOn" multiple>
        <option v-for="task in tasks" :key="task.id" :value="task.id">{{ task.title }}</option>
      </select>
    </label>
    <label class="voko-field">{{ t('board.allowedTools') }}<Textarea v-model="form.allowedTools" :rows="2" /></label>
    <label class="voko-field">{{ t('board.forbidden') }}<Textarea v-model="form.forbidden" :rows="2" /></label>
    <div class="voko-field">
      <span>{{ t('board.template') }}</span>
      <Select v-model="form.template" :aria-label="t('board.template')" :options="['piecework', 'contest', 'race', 'bid'].map((value) => ({ value, label: t(`board.templates.${value}`) }))" />
      <small>{{ t(`board.templateHints.${form.template}`) }}</small>
    </div>
    <template v-if="form.template === 'bid'">
      <label class="voko-field">{{ t('board.bidHours') }}<Input v-model="form.bidHours" type="number" min="0.02" step="1" /></label>
      <div class="voko-field">
        <span>{{ t('board.bidSelection') }}</span>
        <Select v-model="form.bidSelection" :aria-label="t('board.bidSelection')" :options="['pick', 'lowest'].map((value) => ({ value, label: t(`board.bidSelections.${value}`) }))" />
      </div>
    </template>
    <label class="voko-field">{{ t('board.reward_label') }}<Input v-model="form.reward" type="number" /></label>
    <label class="voko-field">{{ t('board.deadline_label') }}<Input v-model="form.deadline" type="datetime-local" /><small>{{ t('board.deadline_hint') }}</small></label>
    <label class="voko-field">{{ t('board.acceptance') }}<Textarea v-model="form.acceptance" :rows="3" /></label>
    <p v-if="message" role="status">{{ message }}</p>
    <p v-for="warning in warnings" :key="warning" role="alert">{{ t('board.warning') }} {{ warning }}</p>
    <Button :disabled="form.title === '' || form.goal === '' || form.spec === '' || form.reward === '' || form.acceptance.trim() === ''" @click="publish">{{ t('board.publish') }}</Button>
  </Card>

</template>

<style scoped>
.voko-board-card {
  margin-bottom: var(--drama-space-md);
}
</style>
